import { Linking, Platform } from 'react-native';
import usePersistedKeyValueStore, { UsePersistedKeyValueStoreArgs } from './usePersistedKeyValueStore';
import { useEffect, useRef, useState } from 'react';
import { APITypes, Dynamo } from '../primary-record-types';
import usePayments from './api/usePayments';

/**
 * When navigating from an invite email link, the email is sent to prefill the email field and prevent the user from entering the wrong email
 */
const emailParamPersistedKeyValueStoreArgs: UsePersistedKeyValueStoreArgs<string | null> = {
    category: 'url-params',
    key: 'email',
    defaultValue: null,
    expiration: 1000 * 60 * 60, // 1 hour
};

/**
 * A promotional registration code that is sent in the url to prefill the registration code field
 */
const registrationCodeParamPersistedKeyValueStoreArgs: UsePersistedKeyValueStoreArgs<string | null> = {
    category: 'url-params',
    key: 'registrationCode',
    defaultValue: null,
    expiration: 1000 * 60 * 60, // 1 hour
};

/**
 * A user type specifier that is sent via the url.  This is needed to determine if a user is a pro user before the organization is ever created.
 */
const userTypeParamPersistedKeyValueStoreArgs: UsePersistedKeyValueStoreArgs<Dynamo.UserType | null> = {
    category: 'url-params',
    key: 'userType',
    defaultValue: null,
    expiration: 1000 * 60 * 60, // 1 hour
};

/**
 * An organization client invitation id that is sent via the url.  This is used to possible show an organization's logo if they have opted into publicly sharing their logo.
 */
const organizationClientInvitationIdParamPersistedKeyValueStoreArgs: UsePersistedKeyValueStoreArgs<string | null> = {
    category: 'url-params',
    key: 'organizationClientInvitationId',
    defaultValue: null,
    expiration: 1000 * 60 * 60, // 1 hour
};

const shareCodeParamPersistedKeyValueStoreArgs: UsePersistedKeyValueStoreArgs<string | null> = {
    category: 'url-params',
    key: 'shareCode',
    defaultValue: null,
    expiration: 1000 * 60 * 60, // 1 hour
};

type UrlAppParams = {
    email: string | null;
    registrationCode: string | null;
    userType: Dynamo.UserType | null;
    organizationClientInvitationId: string | null;
    shareCode: string | null;
};

const getInitialUrl = async (): Promise<string | null> => {
    const initialSearch = Platform.OS === 'web' ? window?.location?.search : (await Linking.getInitialURL()) || '';

    return initialSearch;
};

const getUrlAppParams = async (url: string | null): Promise<UrlAppParams> => {
    const returnValue: UrlAppParams = {
        email: null,
        registrationCode: null,
        userType: null,
        organizationClientInvitationId: null,
        shareCode: null,
    };

    const search = (url || '').split('?')[1];

    if (!search) {
        return returnValue;
    }

    const urlParams = new URLSearchParams(search);
    returnValue.email = urlParams.get('email') || null;
    returnValue.registrationCode = urlParams.get('regcode') || null;
    returnValue.organizationClientInvitationId = urlParams.get('organizationClientInvitationId') || null;
    returnValue.shareCode = urlParams.get('shareCode') || null;

    const isValidUserType = (userType: string): userType is Dynamo.UserType => {
        return ['consumer', 'professional'].includes(userType);
    };
    const rawUserType = urlParams.get('userType');
    if (rawUserType && isValidUserType(rawUserType)) {
        returnValue.userType = rawUserType;
    }

    return returnValue;
};
/**
 * Resets the persisted email from an invite url.  Since the email is stored in local storage, it will persist across sessions and potentially across users if not cleared.
 */
export const useResetUrlEmailState = (): { resetUrlEmailState: () => Promise<void> } => {
    const { setValue } = usePersistedKeyValueStore(emailParamPersistedKeyValueStoreArgs);

    return {
        resetUrlEmailState: () => setValue(null),
    };
};

/**
 * Hook that captures all possible query params that the app may need to use.  This should be used at the start of the app.
 */
export const useCaptureAppQueryParams = (): { isPendingParamCapture: boolean } => {
    const [isPendingParamCapture, setIsPendingParamCapture] = useState(true);
    const { resetUrlEmailState } = useResetUrlEmailState();
    const emailPersistedKeyValueStore = usePersistedKeyValueStore(emailParamPersistedKeyValueStoreArgs);
    const registrationCodePersistedKeyValueStore = usePersistedKeyValueStore(
        registrationCodeParamPersistedKeyValueStoreArgs,
    );
    const userTypePersistedKeyValueStore = usePersistedKeyValueStore(userTypeParamPersistedKeyValueStoreArgs);
    const organizationClientInvitationIdPersistedKeyValueStore = usePersistedKeyValueStore(
        organizationClientInvitationIdParamPersistedKeyValueStoreArgs,
    );
    const shareCodePersistedKeyValueStore = usePersistedKeyValueStore(shareCodeParamPersistedKeyValueStoreArgs);
    const captureUrlParamsRef = useRef(async (url: string | null) => {
        const params = await getUrlAppParams(url);

        if (params.email) {
            await emailPersistedKeyValueStore.setValue(params.email);
        } else {
            await resetUrlEmailState();
        }

        if (params.registrationCode) {
            await registrationCodePersistedKeyValueStore.setValue(params.registrationCode);
        }

        if (params.userType) {
            await userTypePersistedKeyValueStore.setValue(params.userType);
        }

        if (params.organizationClientInvitationId) {
            await organizationClientInvitationIdPersistedKeyValueStore.setValue(params.organizationClientInvitationId);
        }

        if (params.shareCode) {
            await shareCodePersistedKeyValueStore.setValue(params.shareCode);
        }
    });

    useEffect(() => {
        async function captureParamsFromInitialNavigation() {
            const initialUrl = await getInitialUrl();
            await captureUrlParamsRef.current(initialUrl);
            setIsPendingParamCapture(false);
        }

        captureParamsFromInitialNavigation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (Platform.OS === 'web') return;

        // handle navigation back to app from background
        const handleUrlChange = (event: { url: string }) => {
            captureUrlParamsRef.current(event.url);
        };

        Linking.addEventListener('url', handleUrlChange);
    }, []);

    return { isPendingParamCapture };
};

/**
 * Hook to get the email from the url
 */
export const useUrlEmail = () => {
    const { value } = usePersistedKeyValueStore(emailParamPersistedKeyValueStoreArgs);
    return value;
};

/**
 * Hook to determine if social auth should be disabled.  Social auth is disabled if an email is provided in the url.
 *
 * This is to prevent the email from being overridden by the social auth provider.
 */
export const useIsSocialAuthDisabled = () => {
    // if email provided, disable social auth to prevent overriding the explicit email
    return !!useUrlEmail();
};

/**
 * Returns the raw un-validated registration code from the url on from navigation to app
 */
export const useUrlRegistrationCode = () => {
    const { value } = usePersistedKeyValueStore(registrationCodeParamPersistedKeyValueStoreArgs);
    return value;
};

export type NoValidCode = {
    urlRegistrationCodeStatus: 'no-valid-code';
};

export type ValidatingCode = {
    urlRegistrationCodeStatus: 'validating';
};

export type ValidCode = {
    urlRegistrationCodeStatus: 'valid';
    code: string;
    validationResult:
        | APITypes.paymentService.ValidDiscountRegistrationCodeResponse
        | APITypes.paymentService.ValidEntitlementRegistrationCodeResponse;
};

export type UseValidatedUrlRegistrationCodeReturnValue = NoValidCode | ValidatingCode | ValidCode;

/**
 * Hook that returns the validated registration code from the url
 */
export const useValidatedUrlRegistrationCode = (): UseValidatedUrlRegistrationCodeReturnValue => {
    const { value } = usePersistedKeyValueStore(registrationCodeParamPersistedKeyValueStoreArgs);
    const { isValidatingRegistrationCode, registrationCodeValidationResult } = usePayments({
        registrationCodeValidationInput: value || undefined,
    });

    if (!value || registrationCodeValidationResult?.valid === false) {
        return {
            urlRegistrationCodeStatus: 'no-valid-code',
        };
    } else if (isValidatingRegistrationCode) {
        return {
            urlRegistrationCodeStatus: 'validating',
        };
    } else if (registrationCodeValidationResult?.valid === true) {
        return {
            urlRegistrationCodeStatus: 'valid',
            validationResult: registrationCodeValidationResult,
            code: value,
        };
    } else {
        // shouldn't get here
        return {
            urlRegistrationCodeStatus: 'no-valid-code',
        };
    }
};

/**
 * Hook to get the user type from the url
 */
export const useCapturedUrlUserType = (): Dynamo.UserType | null => {
    const { value } = usePersistedKeyValueStore(userTypeParamPersistedKeyValueStoreArgs);
    return value;
};

/**
 * Hook to get the organization client invitation id from the url
 */
export const useOrganizationClientInvitationId = (): string | null => {
    const { value } = usePersistedKeyValueStore(organizationClientInvitationIdParamPersistedKeyValueStoreArgs);
    return value;
};

export const useIsCapturedUrlUserTypeProfessional = (): boolean => {
    const userType = useCapturedUrlUserType();
    return userType === 'professional';
};

export const useSetUrlUserType = () => {
    const { setValue } = usePersistedKeyValueStore(userTypeParamPersistedKeyValueStoreArgs);
    return setValue;
};

export const useCapturedUrlShareCode = (): string | null => {
    const { value } = usePersistedKeyValueStore(shareCodeParamPersistedKeyValueStoreArgs);
    return value;
};
